import { createMemo, For, type JSX, onCleanup, Show, splitProps } from "solid-js"
import { createStore } from "solid-js/store"
import { Popover } from "@kobalte/core/popover"
import { DragDropProvider, PointerSensor } from "@dnd-kit/solid"
import { isSortable, useSortable } from "@dnd-kit/solid/sortable"
import { AutoScroller, Feedback, PointerActivationConstraints } from "@dnd-kit/dom"
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers"
import { RestrictToElement } from "@dnd-kit/dom/modifiers"
import { ScrollView } from "@opencode/ui/scroll-view"
import { ProjectAvatar } from "@opencode/ui/project-avatar"
import { Icon } from "@opencode/ui/icon"
import { IconButton } from "@opencode/ui/icon-button"
import { Button } from "@opencode/ui/button"
import { Spinner } from "@opencode/ui/spinner"
import { Menu } from "@opencode/ui/menu"
import { Tooltip } from "@opencode/ui/tooltip"
import { getProjectAvatarVariant, type HomeProjectSelection, type LocalProject } from "@/shell/state/layout"
import { ServerConnection } from "@/runtime/server/registry"
import { useLanguage } from "@/runtime/i18n/language"
import { canManageServers, usePlatform } from "@/runtime/platform/platform"
import { displayName, getProjectAvatarSource } from "@/shell/layout/helpers"
import { ServerRowMenuView, serverMenuLabels } from "@/servers/registry/row-menu"
import { ServerHealthIndicator } from "@/servers/registry/row"
import { fixedRemoteProjectID } from "@/servers/ssh/name"
import { RemoteWorkspaceMenuItem } from "@/runtime/server/remote"
import { type ServerHealth } from "@/runtime/server/health"
import { fileManagerApp } from "@/home/projects/file-manager"
import type { ProjectCatalogEntry } from "@/workspaces/project-catalog"
import "./view.css"

const HOME_PROJECT_NAV_LABEL = "min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"

const serverContextMenuID = (server: ServerConnection.Any) => `server:${ServerConnection.key(server)}`
const projectContextMenuID = (server: ServerConnection.Any, directory: string) =>
  `project:${ServerConnection.key(server)}:${directory}`
export type HomeProjectsViewProps = {
  dropdown?: boolean
  language: ReturnType<typeof useLanguage>
  servers: ServerConnection.Any[]
  projects: ProjectCatalogEntry[]
  recentlyClosed: ProjectCatalogEntry[]
  selection: HomeProjectSelection
  homedir: (server: ServerConnection.Any) => string
  available: (server: ServerConnection.Any) => boolean
  serverHealth: (server: ServerConnection.Any) => ServerHealth | undefined
  projectsForServer: (server: ServerConnection.Any) => LocalProject[]
  collapsed: (server: ServerConnection.Any) => boolean
  canDefaultServer: boolean
  defaultServerKey: ServerConnection.Key | null | undefined
  canRevealProject: (server: ServerConnection.Any) => boolean
  unseenCount: (server: ServerConnection.Any, project: LocalProject) => number
  onWheel: (event: WheelEvent) => void
  onChooseProject: (server?: ServerConnection.Any) => void
  onFocusServer: (server: ServerConnection.Any) => void
  onAuthenticateServer?: (server: ServerConnection.Any) => void
  onToggleCollapsed: (server: ServerConnection.Any) => void
  onEditServer: (server: ServerConnection.Http) => void
  onSetDefaultServer: (server: ServerConnection.Any | undefined) => void
  canRemoveServer: (server: ServerConnection.Any) => boolean
  onRemoveServer: (server: ServerConnection.Any) => void
  canHideServer: (server: ServerConnection.Any) => boolean
  onHideServer: (server: ServerConnection.Any) => void
  onMoveProject: (key: string, index: number) => void
  onSelectProject: (server: ServerConnection.Any, directory: string) => void
  onAddProjects: (server: ServerConnection.Any, directories: string[]) => void
  onOpenProjectNewSession: (server: ServerConnection.Any, directory: string) => void
  canImportSession: boolean
  onImportSession: (server: ServerConnection.Any, project: LocalProject) => void
  onEditProject: (server: ServerConnection.Any, project: LocalProject) => void
  onRevealProject: (server: ServerConnection.Any, project: LocalProject) => void
  onClearNotifications: (server: ServerConnection.Any, project: LocalProject) => void
  onCloseProject: (server: ServerConnection.Any, directory: string) => void
  onOpenSettings: () => void
}

export function HomeProjectsView(props: HomeProjectsViewProps) {
  const [state, setState] = createStore({ open: false })
  const selected = createMemo(() =>
    props.projects.find(
      (entry) =>
        ServerConnection.key(entry.server) === props.selection.server &&
        entry.project.worktree === props.selection.directory,
    ),
  )
  return (
    <Show when={props.dropdown} fallback={<HomeProjectsPanel {...props} />}>
      <Popover
        open={state.open}
        onOpenChange={(open) => setState("open", open)}
        placement="bottom-start"
        sameWidth
        gutter={6}
      >
        <Popover.Trigger
          data-component="home-projects-dropdown"
          aria-label={props.language.t("home.projects")}
          class="home-projects-dropdown-trigger"
        >
          <Show when={selected()} fallback={<Icon name="folder" size="small" />}>
            {(entry) => (
              <HomeProjectAvatar project={entry().project} remote={!ServerConnection.local(entry().server)} open />
            )}
          </Show>
          <span class="flex min-w-0 flex-1 flex-col leading-[var(--line-height-compact)]">
            <bdi class="truncate">
              <Show when={selected()} fallback={props.language.t("home.projects.all")}>
                {(entry) => <>{displayName(entry().project)}</>}
              </Show>
            </bdi>
          </span>
          <Icon name="chevron-down" size="small" class="shrink-0 text-v2-icon-icon-muted" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            aria-label={props.language.t("home.projects")}
            dir={props.language.direction()}
            class="home-projects-dropdown-content z-50 max-h-[min(70dvh,var(--kb-popper-content-available-height))] overflow-hidden outline-none data-[expanded]:animate-in data-[expanded]:fade-in data-[expanded]:slide-in-from-top-2 duration-150 ease-out motion-reduce:animate-none"
          >
            <HomeProjectsPanel
              {...props}
              onSelectProject={(server, directory) => {
                props.onSelectProject(server, directory)
                setState("open", false)
              }}
              onFocusServer={(server) => {
                props.onFocusServer(server)
                setState("open", false)
              }}
              onAuthenticateServer={(server) => {
                setState("open", false)
                props.onAuthenticateServer?.(server)
              }}
              onChooseProject={(server) => {
                setState("open", false)
                props.onChooseProject(server)
              }}
              onAddProjects={(server, directories) => {
                setState("open", false)
                props.onAddProjects(server, directories)
              }}
              onOpenProjectNewSession={(server, directory) => {
                setState("open", false)
                props.onOpenProjectNewSession(server, directory)
              }}
              onEditProject={(server, project) => {
                setState("open", false)
                props.onEditProject(server, project)
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover>
    </Show>
  )
}

function HomeProjectsPanel(props: HomeProjectsViewProps) {
  const platform = usePlatform()
  const [contextMenu, setContextMenu] = createStore({ open: undefined as string | undefined })
  const contextMenuProps = {
    contextMenuOpen: (id: string) => contextMenu.open === id,
    onSetContextMenuOpen: (id: string, open: boolean) => setContextMenu("open", open ? id : undefined),
  }
  const focused = () =>
    props.servers.find((server) => ServerConnection.key(server) === props.selection.server) ?? props.servers[0]
  return (
    <aside
      class={
        props.dropdown
          ? "flex max-h-[min(60dvh,calc(var(--kb-popper-content-available-height)-12px))] min-h-0 min-w-0 flex-col overflow-hidden"
          : "mt-6 flex min-h-0 min-w-0 flex-col gap-4 overflow-hidden lg:sticky lg:top-14 lg:mt-14 lg:h-[calc(100cqh-56px)] lg:self-start lg:pt-[52px]"
      }
      aria-label={props.language.t("home.projects")}
      onWheel={(event) => {
        if (event.target === event.currentTarget) return
        props.onWheel(event)
      }}
    >
      <Show when={!props.dropdown}>
        <div class="flex h-7 min-w-0 shrink-0 items-center justify-between pl-1.5 pr-3">
          <div class="text-v2-text-text-muted [font-weight:530]">{props.language.t("home.projects")}</div>
          <Tooltip placement="bottom" value={props.language.t("home.project.add")}>
            <IconButton
              data-action="home-add-project"
              variant="ghost-muted"
              size="large"
              class="titlebar-icon [&_[data-slot=icon-svg]]:text-v2-icon-icon-muted"
              icon={<Icon name="folder-add-left" />}
              onClick={() => props.onChooseProject()}
              aria-label={props.language.t("home.project.add")}
            />
          </Tooltip>
        </div>
      </Show>
      <ScrollView data-slot="home-projects-scroll" class="min-h-0 min-w-0 shrink">
        <Show when={props.dropdown && focused()}>
          {(server) => (
            <HomeProjectNavButton
              type="button"
              class="mb-1"
              data-selected={!props.selection.directory ? "" : undefined}
              onClick={() => props.onFocusServer(server())}
            >
              <Icon name="folder" size="small" />
              <span class={HOME_PROJECT_NAV_LABEL}>{props.language.t("home.projects.all")}</span>
            </HomeProjectNavButton>
          )}
        </Show>
        <Show when={props.projects.length > 0} fallback={<HomeProjectEmpty {...props} items={props.recentlyClosed} />}>
          <HomeProjectList {...props} {...contextMenuProps} items={props.projects} />
          <Show when={props.dropdown}>
            <HomeProjectNavButton
              type="button"
              data-action="home-add-project-row"
              class="mt-1"
              onClick={() => props.onChooseProject()}
            >
              <Icon name="folder-add-left" size="small" />
              <span class={HOME_PROJECT_NAV_LABEL}>{props.language.t("home.project.add")}</span>
            </HomeProjectNavButton>
          </Show>
        </Show>
        <For
          each={props.servers.filter(
            (server) => server.type === "ssh" && (server.authenticationRequired || server.connecting),
          )}
        >
          {(server) => (
            <Button
              data-action="home-server-authenticate"
              class="mt-1 w-full"
              size="small"
              variant="neutral"
              disabled={server.type === "ssh" && server.connecting}
              aria-busy={server.type === "ssh" && server.connecting}
              onClick={() => props.onAuthenticateServer?.(server)}
            >
              <Show when={server.type === "ssh" && server.connecting}>
                <Spinner class="size-3.5" />
              </Show>
              {props.language.t(
                server.type === "ssh" && server.connecting
                  ? server.id === fixedRemoteProjectID
                    ? "project.remote.loading"
                    : "ssh.stage.connecting"
                  : "ssh.action.authenticate",
              )}
            </Button>
          )}
        </For>
        <Show when={canManageServers(platform)}>
          <Popover placement="bottom-start" gutter={6}>
            <Popover.Trigger as={HomeProjectNavButton} type="button" class="mt-1" data-action="home-manage-servers">
              <Icon name="settings-gear" size="small" />
              <span class={HOME_PROJECT_NAV_LABEL}>{props.language.t("settings.section.server")}</span>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content class="z-50 min-w-[220px] rounded-md bg-v2-background-bg-base p-1.5 shadow-[var(--v2-elevation-floating)]">
                <For each={props.servers}>
                  {(server) => (
                    <HomeServerRow
                      {...props}
                      {...contextMenuProps}
                      server={server}
                      selected={props.selection.server === ServerConnection.key(server)}
                      health={props.serverHealth(server)}
                    />
                  )}
                </For>
              </Popover.Content>
            </Popover.Portal>
          </Popover>
        </Show>
      </ScrollView>
      <HomeUtilityNav
        class="mb-8 mt-4 hidden shrink-0 lg:flex"
        onOpenSettings={props.onOpenSettings}
        language={props.language}
      />
    </aside>
  )
}

export function HomeUtilityNav(props: {
  class?: string
  onOpenSettings?: () => void
  language: ReturnType<typeof useLanguage>
}) {
  return (
    <div
      class={`${props.class ?? ""} min-w-0 flex-row justify-between gap-1 lg:flex-col lg:justify-start lg:pr-3 [&>button]:w-auto lg:[&>button]:w-full`}
    >
      <Show when={props.onOpenSettings}>
        <HomeProjectNavButton
          type="button"
          class="text-v2-text-text-faint [&>[data-slot=icon-svg]]:text-v2-icon-icon-muted"
          onClick={props.onOpenSettings}
        >
          <Icon name="settings-gear" size="small" />
          <span class={HOME_PROJECT_NAV_LABEL}>{props.language.t("sidebar.settings")}</span>
        </HomeProjectNavButton>
      </Show>
    </div>
  )
}

function HomeServerRow(props: {
  language: HomeProjectsViewProps["language"]
  projectsForServer: HomeProjectsViewProps["projectsForServer"]
  contextMenuOpen: HomeProjectsContextMenuProps["contextMenuOpen"]
  canDefaultServer: HomeProjectsViewProps["canDefaultServer"]
  defaultServerKey: HomeProjectsViewProps["defaultServerKey"]
  onFocusServer: HomeProjectsViewProps["onFocusServer"]
  onToggleCollapsed: HomeProjectsViewProps["onToggleCollapsed"]
  onEditServer: HomeProjectsViewProps["onEditServer"]
  onSetDefaultServer: HomeProjectsViewProps["onSetDefaultServer"]
  canRemoveServer: HomeProjectsViewProps["canRemoveServer"]
  onRemoveServer: HomeProjectsViewProps["onRemoveServer"]
  canHideServer: HomeProjectsViewProps["canHideServer"]
  onHideServer: HomeProjectsViewProps["onHideServer"]
  onSetContextMenuOpen: HomeProjectsContextMenuProps["onSetContextMenuOpen"]
  onChooseProject: HomeProjectsViewProps["onChooseProject"]
  server: ServerConnection.Any
  selected: boolean
  health: ServerHealth | undefined
}) {
  const platform = usePlatform()
  const healthy = () => !!props.health?.healthy
  const authentication = () => props.server.type === "ssh" && props.server.authenticationRequired
  const incompatible = () => !!props.health?.incompatible
  const contextMenuID = () => serverContextMenuID(props.server)
  onCleanup(() => {
    const id = contextMenuID()
    if (props.contextMenuOpen(id)) props.onSetContextMenuOpen(id, false)
  })
  return (
    <Tooltip
      appearance="standard"
      placement="top"
      class="home-project-row-tooltip flex w-full min-w-0"
      inactive={!incompatible() && !authentication()}
      value={
        authentication()
          ? props.language.t("ssh.stage.authentication")
          : props.language.t("server.row.incompatible", { version: props.health?.version ?? "1" })
      }
    >
      <div
        class="group/server relative flex w-full min-w-0 items-center"
        data-home-row
        data-dimmed={!healthy() && !incompatible()}
        data-selected={props.selected ? "" : undefined}
      >
        <HomeProjectNavButton
          type="button"
          data-selected={props.selected ? "" : undefined}
          disabled={!healthy() && !authentication()}
          onClick={() => props.onFocusServer(props.server)}
        >
          <div class="flex size-4 shrink-0 items-center justify-center -mr-0.5">
            <ServerHealthIndicator
              health={props.health}
              connecting={props.server.type === "ssh" && props.server.connecting}
              authenticationRequired={authentication()}
            />
          </div>
          <span
            data-slot="home-row-label"
            class="flex min-w-0 flex-1 items-center gap-1"
            classList={{ "opacity-60": !healthy() && !incompatible() }}
          >
            <span class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {props.server.displayName ?? new URL(props.server.http.url).host}
            </span>
            <Show when={props.server.label}>
              {(label) => (
                <span
                  class={`
                  shrink-0 rounded-[3px] border border-v2-border-border-base px-1 py-0.5
                  text-[11px] leading-[var(--line-height-compact)] text-v2-text-text-muted
                `}
                >
                  {label()}
                </span>
              )}
            </Show>
          </span>
        </HomeProjectNavButton>
        <div
          data-slot="home-row-actions"
          class={`
          hover-reveal ms-auto flex shrink-0 items-center gap-1 pe-1
          group-hover/server:opacity-100 focus-within:opacity-100 data-[menu=true]:opacity-100
        `}
          data-menu={props.contextMenuOpen(contextMenuID())}
        >
          <Show when={canManageServers(platform)}>
            <ServerRowMenuView
              server={props.server}
              labels={serverMenuLabels(props.language)}
              canDefault={props.canDefaultServer}
              isDefault={props.defaultServerKey === ServerConnection.key(props.server)}
              canRemove={props.canRemoveServer(props.server)}
              canHide={props.canHideServer(props.server)}
              onEdit={props.onEditServer}
              onSetDefault={() => props.onSetDefaultServer(props.server)}
              onRemoveDefault={() => props.onSetDefaultServer(undefined)}
              onRemove={() => props.onRemoveServer(props.server)}
              onHide={() => props.onHideServer(props.server)}
              open={props.contextMenuOpen(contextMenuID())}
              onOpenChange={(open) => props.onSetContextMenuOpen(contextMenuID(), open)}
            />
          </Show>
          <Tooltip class="flex shrink-0 items-center" placement="bottom" value={props.language.t("home.project.add")}>
            <IconButton
              data-action="home-add-project"
              variant="ghost-muted"
              size="small"
              icon={<Icon name="folder-add-left" />}
              aria-label={props.language.t("home.project.add")}
              disabled={props.health?.healthy === false && !authentication()}
              onClick={() => props.onChooseProject(props.server)}
            />
          </Tooltip>
        </div>
      </div>
    </Tooltip>
  )
}

type HomeProjectsContextMenuProps = {
  contextMenuOpen: (id: string) => boolean
  onSetContextMenuOpen: (id: string, open: boolean) => void
}

type HomeProjectListProps = HomeProjectsViewProps &
  HomeProjectsContextMenuProps & {
    items: ProjectCatalogEntry[]
  }

function HomeProjectList(props: HomeProjectListProps) {
  let listRef!: HTMLDivElement

  return (
    <DragDropProvider
      sensors={(defaults) => [
        ...defaults.filter((sensor) => sensor !== PointerSensor),
        PointerSensor.configure({
          activationConstraints: (event) =>
            event.pointerType === "touch"
              ? [new PointerActivationConstraints.Delay({ value: 250, tolerance: 5 })]
              : [new PointerActivationConstraints.Distance({ value: 4 })],
          preventActivation: (event) => event.target instanceof Element && !!event.target.closest("[data-action]"),
        }),
      ]}
      modifiers={[RestrictToVerticalAxis, RestrictToElement.configure({ element: () => listRef })]}
      plugins={(defaults) => [
        ...defaults.filter((plugin) => plugin !== AutoScroller && plugin !== Feedback),
        AutoScroller.configure({ acceleration: 8, threshold: { x: 0, y: 0.05 } }),
        Feedback.configure({ dropAnimation: null }),
      ]}
      onDragEnd={(event) => {
        const source = event.operation.source
        if (event.canceled || !isSortable(source)) return
        if (source.initialIndex !== source.index) props.onMoveProject(source.id.toString(), source.index)
      }}
    >
      <div class="flex min-w-0 flex-col gap-1" ref={listRef}>
        {/* Keyed on server and directory: the enriched project objects are
            recreated on every store or sync update, so iterating them directly
            remounts all rows — killing any in-flight drag activation (the
            row's sortable unregisters on unmount) and discarding animations.
            String keys keep row elements alive and move them on reorder. */}
        <For each={props.items.map((entry) => entry.key)}>
          {(key, index) => <HomeProjectSlot {...props} projectKey={key} index={index()} />}
        </For>
      </div>
    </DragDropProvider>
  )
}

function HomeProjectSlot(
  props: HomeProjectListProps & {
    projectKey: string
    index: number
  },
) {
  const initial = props.items.find((item) => item.key === props.projectKey)
  if (!initial) return
  const entry = createMemo<ProjectCatalogEntry>(
    (previous) => props.items.find((item) => item.key === props.projectKey) ?? previous,
    initial,
  )

  return (
    <HomeProjectRow
      {...props}
      projectKey={props.projectKey}
      project={entry().project}
      server={entry().server}
      index={props.index}
      serverSelected={props.selection.server === ServerConnection.key(entry().server)}
      selected={
        props.selection.server === ServerConnection.key(entry().server) &&
        props.selection.directory === entry().project.worktree
      }
      unseen={props.unseenCount(entry().server, entry().project)}
    />
  )
}

function HomeProjectEmpty(
  props: HomeProjectsViewProps & {
    items: ProjectCatalogEntry[]
  },
) {
  return (
    <div class="flex min-w-0 flex-col gap-1">
      <HomeProjectNavButton
        type="button"
        data-action="home-add-project-row"
        class="disabled:opacity-60 [&>[data-slot=icon-svg]]:text-v2-icon-icon-muted"
        onClick={() => props.onChooseProject()}
      >
        <Icon name="folder-add-left" size="small" />
        <span class={HOME_PROJECT_NAV_LABEL}>{props.language.t("home.project.add")}</span>
      </HomeProjectNavButton>
      <Show when={props.items.length > 0}>
        <div class="mt-3 flex h-7 min-w-0 shrink-0 items-center pl-1.5 pr-3">
          <div class="text-v2-text-text-faint [font-weight:530]">{props.language.t("home.recentlyRemoved")}</div>
        </div>
        <For each={props.items}>
          {(entry) => <HomeRecentlyClosedRow {...props} project={entry.project} server={entry.server} />}
        </For>
      </Show>
    </div>
  )
}

function HomeRecentlyClosedRow(
  props: HomeProjectsViewProps & {
    project: LocalProject
    server: ServerConnection.Any
  },
) {
  const unreachable = () =>
    !props.available(props.server) && !(props.server.type === "ssh" && props.server.authenticationRequired)
  const path = () => {
    const home = props.homedir(props.server)
    const worktree = props.project.worktree
    if (home && (worktree === home || worktree.startsWith(`${home}/`))) return `~${worktree.slice(home.length)}`
    return worktree
  }
  return (
    <Tooltip placement="right" value={path()}>
      <HomeProjectNavButton
        type="button"
        data-component="home-recently-closed-row"
        class="disabled:opacity-60"
        disabled={unreachable()}
        onClick={() => props.onAddProjects(props.server, [props.project.worktree])}
      >
        <HomeProjectAvatar project={props.project} remote={!ServerConnection.local(props.server)} outline />
        <span class={HOME_PROJECT_NAV_LABEL}>{displayName(props.project)}</span>
      </HomeProjectNavButton>
    </Tooltip>
  )
}

function HomeProjectRow(
  props: HomeProjectsViewProps &
    HomeProjectsContextMenuProps & {
      project: LocalProject
      projectKey: string
      server: ServerConnection.Any
      index: number
      serverSelected: boolean
      selected: boolean
      unseen: number
    },
) {
  const platform = usePlatform()
  const serverUnreachable = () => !props.available(props.server)
  const authentication = () => props.server.type === "ssh" && props.server.authenticationRequired
  const sortable = useSortable({
    get id() {
      return props.projectKey
    },
    get index() {
      return props.index
    },
  })
  let pointerDownSelected: boolean | undefined
  const contextMenuID = () => projectContextMenuID(props.server, props.project.worktree)
  onCleanup(() => {
    const id = contextMenuID()
    if (props.contextMenuOpen(id)) props.onSetContextMenuOpen(id, false)
  })
  return (
    <div
      ref={sortable.ref}
      class="group/project relative flex min-w-0 items-center"
      classList={{ "z-10": sortable.isDragSource() }}
      data-home-row
      data-dimmed={serverUnreachable()}
      data-dragging={sortable.isDragSource()}
      data-selected={props.selected ? "" : undefined}
      onContextMenu={(event) => {
        event.preventDefault()
        props.onSetContextMenuOpen(contextMenuID(), true)
      }}
    >
      <HomeProjectNavButton
        type="button"
        data-component="home-project-row"
        class="disabled:opacity-60"
        classList={{
          "bg-v2-background-bg-layer-01 text-v2-text-text-base": sortable.isDragSource(),
        }}
        data-selected={props.selected ? "" : undefined}
        aria-current={props.selected ? "page" : undefined}
        disabled={serverUnreachable() && !authentication()}
        onPointerDown={(event) => {
          // Same-server mouse selection happens on pointerdown (like tabs),
          // but only ever selects; selectProject toggles, and deselecting here
          // would fire on every drag before the threshold is met. Cross-server
          // selection waits for click so reordering a remote server's projects
          // does not focus that server and load its session index. Touch is
          // excluded so flick-scrolling the list cannot select rows.
          pointerDownSelected = undefined
          if (props.dropdown) return
          if (event.button !== 0 || event.pointerType === "touch") return
          if (!props.serverSelected) return
          pointerDownSelected = props.selected
          if (!props.selected) props.onSelectProject(props.server, props.project.worktree)
        }}
        onClick={(event) => {
          // The drag sensor calls preventDefault on post-drag clicks; never
          // toggle selection as part of a reorder.
          if (event.defaultPrevented) return
          // Keyboard activation and touch taps keep the original toggle.
          if (event.detail === 0 || pointerDownSelected === undefined) {
            props.onSelectProject(props.server, props.project.worktree)
            return
          }
          // Mouse: pointerdown already selected unselected rows; a plain click
          // on an already-selected row toggles it off.
          if (pointerDownSelected) props.onSelectProject(props.server, props.project.worktree)
          pointerDownSelected = undefined
        }}
      >
        <HomeProjectAvatar
          project={props.project}
          remote={!ServerConnection.local(props.server)}
          open={props.selected}
        />
        <span data-slot="home-row-label" class={HOME_PROJECT_NAV_LABEL}>
          {displayName(props.project)}
        </span>
      </HomeProjectNavButton>
      <div
        data-slot="home-row-actions"
        class={`
          hover-reveal ms-auto flex shrink-0 items-center gap-1 pe-1
          group-hover/project:opacity-100 focus-within:opacity-100 data-[menu=true]:opacity-100
        `}
        data-menu={props.contextMenuOpen(contextMenuID())}
      >
        <Menu
          gutter={6}
          modal={false}
          placement="bottom-end"
          open={props.contextMenuOpen(contextMenuID())}
          onOpenChange={(open) => props.onSetContextMenuOpen(contextMenuID(), open)}
        >
          <Menu.Trigger
            as={IconButton}
            data-action="home-project-menu"
            variant="ghost-muted"
            size="small"
            icon={<Icon name="outline-dots" />}
            aria-label={props.language.t("common.moreOptions")}
          />
          <Menu.Portal>
            <Menu.Content>
              <Menu.Item
                disabled={serverUnreachable() && !authentication()}
                onSelect={() => props.onOpenProjectNewSession(props.server, props.project.worktree)}
              >
                {props.language.t("command.session.new")}
              </Menu.Item>
              <Show when={props.canImportSession}>
                <Menu.Item
                  disabled={serverUnreachable()}
                  onSelect={() => props.onImportSession(props.server, props.project)}
                >
                  {props.language.t("command.session.import")}
                </Menu.Item>
              </Show>
              <Menu.Item
                disabled={serverUnreachable()}
                onSelect={() => props.onEditProject(props.server, props.project)}
              >
                {props.language.t("dialog.project.edit.title")}
              </Menu.Item>
              <Show when={props.canRevealProject(props.server)}>
                <Menu.Item onSelect={() => props.onRevealProject(props.server, props.project)}>
                  {props.language.t(
                    fileManagerApp(platform.platform === "desktop" ? (platform.os ?? "unknown") : "unknown")
                      .actionLabel,
                  )}
                </Menu.Item>
              </Show>
              <Menu.Item
                disabled={props.unseen === 0}
                onSelect={() => props.onClearNotifications(props.server, props.project)}
              >
                {props.language.t("sidebar.project.clearNotifications")}
              </Menu.Item>
              <RemoteWorkspaceMenuItem server={props.server} />
              <Menu.Separator />
              <Menu.Item onSelect={() => props.onCloseProject(props.server, props.project.worktree)}>
                {props.language.t("session.navigation.removeProject")}
              </Menu.Item>
            </Menu.Content>
          </Menu.Portal>
        </Menu>
        <IconButton
          data-action="home-project-new-session"
          variant="ghost-muted"
          size="small"
          icon={<Icon name="edit" />}
          aria-label={props.language.t("command.session.new")}
          disabled={serverUnreachable() && !authentication()}
          onClick={() => props.onOpenProjectNewSession(props.server, props.project.worktree)}
        />
      </div>
    </div>
  )
}

function HomeProjectNavButton(props: JSX.ButtonHTMLAttributes<HTMLButtonElement>) {
  const [local, rest] = splitProps(props, ["class", "classList", "children"])
  return (
    <button {...rest} class={`home-project-nav-button ${local.class ?? ""}`} classList={local.classList}>
      {local.children}
    </button>
  )
}

function HomeProjectAvatar(props: { project: LocalProject; remote: boolean; open?: boolean; outline?: boolean }) {
  const name = createMemo(() => displayName(props.project))
  return (
    <ProjectAvatar
      fallback={name()}
      src={getProjectAvatarSource(props.project.id, props.project.icon)}
      variant={
        props.outline && !props.project.icon?.color ? "outline" : getProjectAvatarVariant(props.project.icon?.color)
      }
      remote={props.remote}
      open={props.open}
    />
  )
}
